<template>
  <el-card shadow="never" class="panel-card" style="overflow: visible;">
    <div slot="header">S1/S2 sensor chart</div>
    <div class="chart-shell chart-shell-lg">
      <div class="chart-frame chart-frame-echarts">
        <div ref="chartContainer" class="echarts-surface"></div>
        <div v-if="loading" class="chart-loading-overlay">
          <i class="el-icon-loading chart-loading-spinner"></i>
          <span>Loading chart data...</span>
        </div>

        <div
          v-if="hasPlotRect"
          class="chart-overlay-layer"
          :style="plotRectStyle"
        >
          <div
            v-for="block in currentCycle.blocks"
            :key="'combined-block-' + block.blockNumber"
            class="block-band"
            :style="getBlockOverlayStyle(block)"
          ></div>
          <div
            v-for="(region, index) in regions"
            :key="region.id + '-overlay'"
            class="region-overlay"
            :class="{
              'region-overlay-active':
                quickReasonRegion && quickReasonRegion.id === region.id,
              'region-overlay-read-only': readOnly
            }"
            :style="getRegionOverlayStyle(region)"
            @mousedown.stop="startRegionInteraction('move', region, $event)"
          >
            <span
              v-if="!readOnly"
              class="region-resize-handle region-resize-handle-left"
              @mousedown.stop="
                startRegionInteraction('resize-left', region, $event)
              "
            ></span>
            <span
              v-if="!readOnly"
              class="region-resize-handle region-resize-handle-right"
              @mousedown.stop="
                startRegionInteraction('resize-right', region, $event)
              "
            ></span>
            <button
              v-if="!readOnly"
              type="button"
              class="region-remove-button"
              @mousedown.stop
              @mouseup.stop
              @click.stop="handleRegionRemove(region.id)"
            >
              ×
            </button>
            <span
              class="region-overlay-label"
              @mousedown.stop
              @click.stop="handleRegionClick(region.id)"
            >
              <span
                v-for="reasonLabel in regionOverlayLabelsFn(region)"
                :key="
                  region.id +
                    '-label-' +
                    getOverlayLabelKey(reasonLabel, index) +
                    '-' +
                    index
                "
                class="region-overlay-label-line"
                :style="getOverlayLabelStyle(reasonLabel)"
              >
                <span
                  v-if="getOverlayLabelMarkerStyle(reasonLabel)"
                  class="region-overlay-label-marker"
                  :style="getOverlayLabelMarkerStyle(reasonLabel)"
                ></span>
                {{ getOverlayLabelText(reasonLabel) }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="quickReasonRegion && !readOnly"
        ref="quickReasonPanel"
        class="quick-reason-panel"
        :style="resolvedQuickReasonPanelStyle"
      >
        <div class="quick-reason-header">
          <div class="quick-reason-title quick-reason-title-primary">
            1. Select Sensor
          </div>
          <button
            type="button"
            class="quick-reason-remove-button"
            @click="handleRegionRemove(quickReasonRegion.id)"
          >
            Remove
          </button>
        </div>
        <div class="quick-sensor-options">
          <button
            v-for="sensorOption in sensorOptions"
            :key="quickReasonRegion.id + '-sensor-' + sensorOption.value"
            type="button"
            class="quick-sensor-option"
            :class="{
              active: quickReasonRegion.sensor === sensorOption.value
            }"
            :style="getSensorOptionStyle(sensorOption)"
            @click="
              $emit(
                'update-region-sensor',
                quickReasonRegion.id,
                sensorOption.value
              )
            "
          >
            {{ sensorOption.label }}
          </button>
        </div>
        <div class="quick-reason-title quick-reason-subtitle">
          2. Select Reason
        </div>
        <div class="quick-reason-options">
          <button
            v-for="reason in reasonCodes"
            :key="quickReasonRegion.id + '-quick-' + reason.id"
            type="button"
            class="quick-reason-option"
            :class="{
              active: quickReasonRegion.reasonCodes.indexOf(reason.code) > -1
            }"
            @click="
              $emit('toggle-region-reason', quickReasonRegion.id, reason.code)
            "
          >
            {{ reason.label }}
          </button>
        </div>
        <el-button
          type="primary"
          class="quick-reason-save-button"
          :disabled="!isQuickReasonSelectionComplete"
          @click="$emit('close-quick-reason-picker')"
        >
          Save
        </el-button>
      </div>

      <div
        v-if="hoveredBlock"
        ref="hoveredBlockCard"
        class="hovered-block-card"
        :style="resolvedHoveredBlockCardStyle"
      >
        <div class="hovered-block-card-header">
          <strong>Block {{ hoveredBlock.blockNumber || '--' }}</strong>
          <div class="hovered-block-card-actions">
            <span
              v-if="
                hoveredBlock.prediction && hoveredBlock.prediction !== 'UNKNOWN'
              "
              class="hovered-block-prediction"
              :class="getPredictionClassName(hoveredBlock.prediction)"
            >
              {{ hoveredBlock.prediction }}
            </span>
            <button
              type="button"
              class="hovered-block-close-button"
              aria-label="Close block information"
              @click.stop="$emit('close-block-info')"
            >
              ×
            </button>
          </div>
        </div>
        <div class="hovered-block-grid">
          <div
            v-for="metric in getHoveredBlockMetrics(hoveredBlock)"
            :key="metric.key"
            class="hovered-block-metric"
          >
            <span class="hovered-block-metric-label">{{ metric.label }}</span>
            <strong class="hovered-block-metric-value">
              {{ formatBlockMetricValue(metric.value) }}
            </strong>
          </div>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script>
import moment from 'moment-timezone'

export default {
  name: 'LabellingChartPanel',
  data() {
    return {
      chart: null,
      zr: null,
      plotRect: null,
      visibleStartTime: null,
      visibleEndTime: null,
      regionInteraction: null,
      quickReasonPanelSize: {
        width: 320,
        height: 220
      },
      hoveredBlockCardSize: {
        width: 260,
        height: 220
      }
    }
  },
  props: {
    currentCycle: {
      type: Object,
      required: true
    },
    chartReadings: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    regions: {
      type: Array,
      default: () => []
    },
    reasonCodes: {
      type: Array,
      default: () => []
    },
    hoveredBlock: {
      type: Object,
      default: null
    },
    blockInfoAnchor: {
      type: Object,
      default: null
    },
    quickReasonRegion: {
      type: Object,
      default: null
    },
    regionOverlayLabelsFn: {
      type: Function,
      required: true
    },
    sensorOptions: {
      type: Array,
      default: () => []
    },
    blockInfoTrigger: {
      type: String,
      default: 'none'
    },
    blockOverlayStyleFn: {
      type: Function,
      default: null
    }
  },
  computed: {
    hasPlotRect() {
      return !!this.plotRect
    },
    isQuickReasonSelectionComplete() {
      if (!this.quickReasonRegion) {
        return false
      }
      return (
        !!this.quickReasonRegion.sensor &&
        Array.isArray(this.quickReasonRegion.reasonCodes) &&
        this.quickReasonRegion.reasonCodes.length > 0
      )
    },
    plotRectStyle() {
      if (!this.plotRect) {
        return {}
      }
      return {
        left: this.plotRect.left + 'px',
        top: this.plotRect.top + 'px',
        width: this.plotRect.width + 'px',
        height: this.plotRect.height + 'px'
      }
    },
    resolvedQuickReasonPanelStyle() {
      if (!this.quickReasonRegion || !this.plotRect) {
        return {}
      }
      const regionStyle = this.getOverlayPositionStyle(
        this.quickReasonRegion.startSec,
        this.quickReasonRegion.endSec
      )
      if (
        !regionStyle ||
        regionStyle.display === 'none' ||
        regionStyle.left === undefined ||
        regionStyle.width === undefined
      ) {
        return {}
      }
      const regionLeft = parseFloat(regionStyle.left)
      const regionWidth = parseFloat(regionStyle.width)
      if (!Number.isFinite(regionLeft) || !Number.isFinite(regionWidth)) {
        return {}
      }
      const chartWidth = this.$refs.chartContainer
        ? this.$refs.chartContainer.clientWidth
        : 0
      const chartHeight = this.$refs.chartContainer
        ? this.$refs.chartContainer.clientHeight
        : 0
      const panelWidth = this.quickReasonPanelSize.width || 320
      const panelHeight = this.quickReasonPanelSize.height || 220
      const edgePadding = 12
      const popupGap = 16
      const anchorLeft = this.plotRect.left + regionLeft + regionWidth / 2
      const anchorTop = this.plotRect.top + this.plotRect.height / 2
      const preferredLeft = anchorLeft + regionWidth / 2 + popupGap
      const fallbackLeft = anchorLeft - regionWidth / 2 - panelWidth - popupGap
      const maxLeft = Math.max(
        chartWidth - panelWidth - edgePadding,
        edgePadding
      )
      const resolvedLeft =
        preferredLeft + panelWidth + edgePadding <= chartWidth
          ? preferredLeft
          : fallbackLeft
      const resolvedTop = Math.min(
        Math.max(anchorTop - panelHeight / 2, edgePadding),
        Math.max(chartHeight - panelHeight - edgePadding, edgePadding)
      )
      return {
        left: Math.min(Math.max(resolvedLeft, edgePadding), maxLeft) + 'px',
        top: resolvedTop + 'px',
        right: 'auto'
      }
    },
    resolvedHoveredBlockCardStyle() {
      if (!this.hoveredBlock || !this.blockInfoAnchor) {
        return {}
      }
      const chartWidth = Number(this.blockInfoAnchor.chartWidth) || 0
      const chartHeight = Number(this.blockInfoAnchor.chartHeight) || 0
      const anchorLeft = Number(this.blockInfoAnchor.leftPx)
      const anchorTop = Number(this.blockInfoAnchor.topPx)
      if (
        !Number.isFinite(chartWidth) ||
        !Number.isFinite(chartHeight) ||
        !Number.isFinite(anchorLeft) ||
        !Number.isFinite(anchorTop)
      ) {
        return {}
      }
      const cardWidth = this.hoveredBlockCardSize.width || 260
      const cardHeight = this.hoveredBlockCardSize.height || 220
      const edgePadding = 12
      const popupGap = 14
      const preferredLeft = anchorLeft + popupGap
      const fallbackLeft = anchorLeft - cardWidth - popupGap
      const maxLeft = Math.max(
        chartWidth - cardWidth - edgePadding,
        edgePadding
      )
      const resolvedLeft =
        preferredLeft + cardWidth + edgePadding <= chartWidth
          ? preferredLeft
          : fallbackLeft
      const preferredTop = anchorTop + popupGap
      const fallbackTop = anchorTop - cardHeight - popupGap
      const maxTop = Math.max(
        chartHeight - cardHeight - edgePadding,
        edgePadding
      )
      const resolvedTop =
        preferredTop + cardHeight + edgePadding <= chartHeight
          ? preferredTop
          : fallbackTop
      return {
        left: Math.min(Math.max(resolvedLeft, edgePadding), maxLeft) + 'px',
        top: Math.min(Math.max(resolvedTop, edgePadding), maxTop) + 'px',
        right: 'auto'
      }
    }
  },
  watch: {
    quickReasonRegion() {
      if (!this.quickReasonRegion) {
        return
      }
      this.$nextTick(this.updateQuickReasonPanelSize)
    },
    hoveredBlock() {
      if (!this.hoveredBlock) {
        return
      }
      this.$nextTick(this.updateHoveredBlockCardSize)
    },
    currentCycle: {
      handler() {
        this.$nextTick(() => {
          this.resetZoomWindow()
          this.renderChart()
        })
      },
      deep: true
    },
    chartReadings: {
      handler() {
        this.$nextTick(() => {
          this.resetZoomWindow()
          this.renderChart()
        })
      },
      deep: true
    },
    regions: {
      handler() {
        this.$nextTick(this.updatePlotRect)
      },
      deep: true
    },
    readOnly: {
      handler(value) {
        if (value) {
          this.clearBrushSelection()
        }
        this.$nextTick(this.syncBrushState)
      },
      immediate: true
    },
    blockInfoTrigger() {
      this.bindBlockInfoEvents()
    }
  },
  mounted() {
    this.initChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.stopRegionInteraction()
    if (this.zr) {
      this.unbindBlockInfoEvents()
      this.zr = null
    }
    if (this.chart) {
      this.chart.off('datazoom')
      this.chart.off('brushEnd', this.handleBrushEnd)
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    initChart() {
      if (!this.$refs.chartContainer) {
        return
      }
      this.chart = this.echarts.init(this.$refs.chartContainer)
      this.chart.on('datazoom', this.handleDataZoom)
      this.chart.on('brushEnd', this.handleBrushEnd)
      this.zr = this.chart.getZr()
      this.bindBlockInfoEvents()
      this.renderChart()
    },
    bindBlockInfoEvents() {
      if (!this.zr) {
        return
      }
      this.unbindBlockInfoEvents()
      if (this.blockInfoTrigger === 'hover') {
        this.zr.on('mousemove', this.handleZrMouseMove)
        this.zr.on('globalout', this.handleZrMouseLeave)
        return
      }
      if (this.blockInfoTrigger === 'click') {
        this.zr.on('click', this.handleZrClick)
      }
    },
    unbindBlockInfoEvents() {
      if (!this.zr) {
        return
      }
      this.zr.off('mousemove', this.handleZrMouseMove)
      this.zr.off('globalout', this.handleZrMouseLeave)
      this.zr.off('click', this.handleZrClick)
    },
    handleResize() {
      if (!this.chart) {
        return
      }
      this.chart.resize()
      this.updatePlotRect()
      this.updateQuickReasonPanelSize()
      this.updateHoveredBlockCardSize()
    },
    updateQuickReasonPanelSize() {
      const panel = this.$refs.quickReasonPanel
      if (!panel) {
        return
      }
      this.quickReasonPanelSize = {
        width: panel.offsetWidth || 320,
        height: panel.offsetHeight || 220
      }
    },
    updateHoveredBlockCardSize() {
      const card = this.$refs.hoveredBlockCard
      if (!card) {
        return
      }
      this.hoveredBlockCardSize = {
        width: card.offsetWidth || 260,
        height: card.offsetHeight || 220
      }
    },
    resetZoomWindow() {
      const firstReading = this.chartReadings[0]
      const lastReading = this.chartReadings[this.chartReadings.length - 1]
      this.visibleStartTime = firstReading
        ? this.getReadingTimeValue(firstReading.timeStamp)
        : null
      this.visibleEndTime = lastReading
        ? this.getReadingTimeValue(lastReading.timeStamp)
        : null
    },
    renderChart() {
      if (!this.chart) {
        return
      }
      this.chart.setOption(this.buildChartOption(), true)
      this.syncBrushState()
      this.$nextTick(this.updatePlotRect)
    },
    buildChartOption() {
      const chartSource = this.buildChartSource()
      return {
        animation: false,
        grid: {
          left: 0,
          right: 20,
          bottom: 50,
          top: 30,
          containLabel: true
        },
        toolbox: {
          show: chartSource[1] && chartSource[1][0],
          iconStyle: {
            borderColor: 'rgba(125, 211, 252, 0.8)'
          },
          emphasis: {
            iconStyle: {
              borderColor: '#7dd3fc'
            }
          },
          feature: {
            brush: {
              title: {
                lineX: 'Select',
                clear: 'Clear selected'
              }
            }
          }
        },
        tooltip: {
          show: this.blockInfoTrigger !== 'none',
          trigger: 'axis',
          triggerOn:
            this.blockInfoTrigger === 'click' ? 'click' : 'mousemove|click',
          confine: true,
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(148, 163, 184, 0.45)',
              width: 1
            }
          },
          backgroundColor: 'rgba(15, 23, 42, 0.96)',
          borderColor: 'rgba(148, 163, 184, 0.25)',
          textStyle: {
            color: '#e2e8f0'
          },
          formatter: params => this.formatCapacitanceTooltip(params)
        },
        color: ['#008ffb', '#00e396'],
        legend: {
          x: 'center',
          y: 'bottom',
          bottom: 0,
          icon: 'roundRect',
          itemWidth: 18,
          itemHeight: 3,
          textStyle: {
            color: '#e2e8f0',
            fontSize: 11,
            fontWeight: 600
          },
          data: ['S1', 'S2']
        },
        dataZoom: [
          {
            show: true,
            height: 25,
            bottom: 25,
            start: 0,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            borderColor: 'rgba(148, 163, 184, 0.16)',
            fillerColor: 'rgba(56, 189, 248, 0.18)',
            backgroundColor: 'rgba(15, 23, 42, 0.72)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.3)'
              },
              areaStyle: {
                color: 'rgba(148, 163, 184, 0.08)'
              }
            },
            textStyle: {
              color: 'rgba(226, 232, 240, 0.7)'
            },
            handleStyle: {
              color: '#38bdf8',
              borderColor: '#7dd3fc'
            },
            moveHandleStyle: {
              color: '#38bdf8'
            },
            startValue: this.visibleStartTime,
            endValue: this.visibleEndTime
          },
          {
            type: 'inside',
            xAxisIndex: 0,
            zoomOnMouseWheel: true,
            moveOnMouseMove: false,
            moveOnMouseWheel: false,
            startValue: this.visibleStartTime,
            endValue: this.visibleEndTime
          }
        ],
        dataset: {
          dimensions: [
            'timeStampValue',
            'sensor1Value',
            'sensor2Value',
            'timeStampWithUtc',
            'timeStamp'
          ],
          source: chartSource
        },
        brush: {
          toolbox: ['lineX', 'clear'],
          xAxisIndex: 0,
          throttleType: 'debounce',
          throttleDelay: 200,
          brushStyle: {
            borderWidth: 2,
            color: 'rgba(255, 255, 0, 0.15)',
            borderColor: '#ec808d'
          }
        },
        xAxis: [
          {
            type: 'time',
            boundaryGap: false,
            axisLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.45)'
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.08)'
              }
            },
            axisLabel: {
              show: true,
              showMinLabel: true,
              showMaxLabel: true,
              color: 'rgba(226, 232, 240, 0.75)',
              formatter: value => this.formatAxisLabel(value)
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            nameLocation: 'center',
            nameGap: 40,
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: 'rgba(226, 232, 240, 0.6)'
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: ['rgba(148, 163, 184, 0.12)'],
                width: 1,
                type: 'solid'
              }
            }
          }
        ],
        series: [
          {
            name: 'S1',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampValue',
              y: 'sensor1Value'
            }
          },
          {
            name: 'S2',
            type: 'line',
            symbol: 'circle',
            smooth: true,
            encode: {
              x: 'timeStampValue',
              y: 'sensor2Value'
            }
          }
        ]
      }
    },
    formatCapacitanceTooltip(params) {
      const rows = Array.isArray(params) ? params : [params]
      if (!rows.length) {
        return ''
      }
      let tooltipStr =
        '<div>' +
        this.formatUtcTimestamp(rows[0].axisValue, 'DD/MM/YY HH:mm:ss') +
        '</div>'
      rows.forEach(item => {
        if (tooltipStr.indexOf(item.color) < 0) {
          tooltipStr =
            tooltipStr +
            '<div><div style="display:inline-block; margin-right: 5px; width: 10px; height: 10px; border-radius: 5px; background-color:' +
            item.color +
            '"></div>' +
            item.seriesName +
            ' : ' +
            item.value[item.seriesIndex + 1] +
            '</div>'
        }
      })
      return tooltipStr
    },
    buildChartSource() {
      const source = [
        [
          'timeStampValue',
          'sensor1Value',
          'sensor2Value',
          'timeStampWithUtc',
          'timeStamp'
        ]
      ]
      this.chartReadings.forEach(reading => {
        const timeStampValue = this.getReadingTimeValue(reading.timeStamp)
        if (!Number.isFinite(timeStampValue)) {
          return
        }
        source.push([
          timeStampValue,
          reading.sensor1Value,
          reading.sensor2Value,
          this.formatApiTimestamp(reading.timeStamp),
          reading.timeStamp
        ])
      })
      if (source.length <= 1) {
        source.push([null, 0, 0, '', ''])
      }
      return source
    },
    getReadingTimeValue(value) {
      return this.parseUtcTimestamp(value)
    },
    parseUtcTimestamp(value) {
      if (value === null || value === undefined || value === '') {
        return NaN
      }
      const utcTime = moment.utc(value).valueOf()
      if (Number.isFinite(utcTime)) {
        return utcTime
      }
      const nativeTime = new Date(value).getTime()
      return Number.isFinite(nativeTime) ? nativeTime : NaN
    },
    formatApiTimestamp(value) {
      if (!value) {
        return '--'
      }
      return this.formatUtcTimestamp(value, 'YYYY-MM-DD HH:mm:ss')
    },
    formatAxisLabel(value) {
      const firstReadingTime = this.getReadingTimeValue(
        (this.chartReadings[0] || {}).timeStamp
      )
      if (!Number.isFinite(firstReadingTime)) {
        return '0 min'
      }
      const axisTime = Number(value)
      if (!Number.isFinite(axisTime)) {
        return '0 min'
      }
      const totalMinutes = Math.max(
        Math.round((axisTime - firstReadingTime) / 60000),
        0
      )
      if (totalMinutes < 60) {
        return totalMinutes + ' min'
      }
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (!minutes) {
        return hours + ' hr'
      }
      return hours + ' hr ' + minutes + ' min'
    },
    formatUtcTimestamp(value, format) {
      if (!value) {
        return '--'
      }
      const timestamp = this.parseUtcTimestamp(value)
      return Number.isNaN(timestamp)
        ? value
        : moment.utc(timestamp).format(format)
    },
    handleDataZoom() {
      if (!this.chart) {
        return
      }
      const option = this.chart.getOption()
      const dataZoom = option && option.dataZoom ? option.dataZoom[0] : null
      if (dataZoom) {
        this.visibleStartTime =
          dataZoom.startValue !== undefined
            ? dataZoom.startValue
            : this.visibleStartTime
        this.visibleEndTime =
          dataZoom.endValue !== undefined
            ? dataZoom.endValue
            : this.visibleEndTime
      }
      this.updatePlotRect()
    },
    syncBrushState() {
      if (!this.chart) {
        return
      }
      if (this.readOnly) {
        this.chart.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'brush',
          brushOption: false
        })
        this.clearBrushSelection()
        return
      }
      this.chart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'brush',
        brushOption: {
          brushType: 'lineX',
          brushMode: 'single'
        }
      })
    },
    clearBrushSelection() {
      if (!this.chart) {
        return
      }
      this.chart.dispatchAction({
        type: 'brush',
        areas: []
      })
    },
    handleBrushEnd(params) {
      if (this.readOnly) {
        return
      }
      const areas = params && params.areas ? params.areas : []
      if (!areas.length || !this.currentCycle || !this.currentCycle.startDate) {
        return
      }
      const area = areas[0]
      const coordRange = area.coordRange
      if (!Array.isArray(coordRange) || coordRange.length < 2) {
        return
      }
      const cycleStart = this.parseUtcTimestamp(this.currentCycle.startDate)
      const startTime = this.parseAxisTime(coordRange[0])
      const endTime = this.parseAxisTime(coordRange[1])
      if (
        Number.isNaN(cycleStart) ||
        Number.isNaN(startTime) ||
        Number.isNaN(endTime)
      ) {
        return
      }
      const normalizedStartTime = Math.min(startTime, endTime)
      const normalizedEndTime = Math.max(startTime, endTime)
      const range = {
        startSec: Math.max(
          Math.round((normalizedStartTime - cycleStart) / 1000),
          0
        ),
        endSec: Math.max(
          Math.round((normalizedEndTime - cycleStart) / 1000),
          0
        ),
        startTime: new Date(normalizedStartTime).toISOString(),
        endTime: new Date(normalizedEndTime).toISOString()
      }
      this.$emit('chart-leave')
      this.$emit('select-range', range)
      this.clearBrushSelection()
    },
    updatePlotRect() {
      if (!this.chart || !this.chartReadings.length) {
        this.plotRect = null
        return
      }
      const gridComponent =
        this.chart.getModel && this.chart.getModel().getComponent('grid', 0)
      const gridRect =
        gridComponent &&
        gridComponent.coordinateSystem &&
        gridComponent.coordinateSystem.getRect
          ? gridComponent.coordinateSystem.getRect()
          : null
      if (
        gridRect &&
        [gridRect.x, gridRect.y, gridRect.width, gridRect.height].every(value =>
          Number.isFinite(value)
        )
      ) {
        this.plotRect = {
          left: gridRect.x,
          top: gridRect.y,
          width: gridRect.width,
          height: gridRect.height
        }
        return
      }
      const range = this.getValueRange()
      const startTime = this.getVisibleStartTime()
      const endTime = this.getVisibleEndTime()
      const left = this.chart.convertToPixel({ xAxisIndex: 0 }, startTime)
      const right = this.chart.convertToPixel({ xAxisIndex: 0 }, endTime)
      const top = this.chart.convertToPixel({ yAxisIndex: 0 }, range.max)
      const bottom = this.chart.convertToPixel({ yAxisIndex: 0 }, range.min)
      if (
        [left, right, top, bottom].some(value => !Number.isFinite(value)) ||
        right <= left ||
        bottom <= top
      ) {
        this.plotRect = null
        return
      }
      this.plotRect = {
        left,
        top,
        width: right - left,
        height: bottom - top
      }
    },
    getVisibleStartTime() {
      return (
        this.visibleStartTime ||
        this.getReadingTimeValue((this.chartReadings[0] || {}).timeStamp) ||
        null
      )
    },
    getVisibleEndTime() {
      const lastReading = this.chartReadings[this.chartReadings.length - 1]
      return (
        this.visibleEndTime ||
        (lastReading ? this.getReadingTimeValue(lastReading.timeStamp) : null)
      )
    },
    getValueRange() {
      if (!this.chartReadings.length) {
        return { min: 0, max: 1 }
      }
      const values = this.chartReadings.reduce((items, reading) => {
        items.push(reading.sensor1Value || 0, reading.sensor2Value || 0)
        return items
      }, [])
      const min = Math.min(...values)
      const max = Math.max(...values)
      return {
        min,
        max: max === min ? max + 1 : max
      }
    },
    getSecondsFromOffsetX(offsetX) {
      if (!this.chart || !this.currentCycle || !this.plotRect) {
        return 0
      }
      const pixelX = Math.min(
        Math.max(offsetX, this.plotRect.left),
        this.plotRect.left + this.plotRect.width
      )
      const axisValue = this.chart.convertFromPixel({ xAxisIndex: 0 }, pixelX)
      const axisTime = this.parseAxisTime(axisValue)
      const cycleStart = this.parseUtcTimestamp(this.currentCycle.startDate)
      if (Number.isNaN(axisTime) || Number.isNaN(cycleStart)) {
        return 0
      }
      return Math.max(Math.round((axisTime - cycleStart) / 1000), 0)
    },
    getSecondsFromClientX(clientX) {
      if (!this.$refs.chartContainer) {
        return 0
      }
      const chartRect = this.$refs.chartContainer.getBoundingClientRect()
      return this.getSecondsFromOffsetX(clientX - chartRect.left)
    },
    parseAxisTime(axisValue) {
      if (typeof axisValue === 'number') {
        return axisValue
      }
      return this.parseUtcTimestamp(axisValue)
    },
    getTimeValueFromSecond(second) {
      if (!this.currentCycle || !this.currentCycle.startDate) {
        return NaN
      }
      const cycleStart = this.parseUtcTimestamp(this.currentCycle.startDate)
      if (Number.isNaN(cycleStart)) {
        return NaN
      }
      return cycleStart + second * 1000
    },
    getOverlayPositionStyle(startSec, endSec) {
      if (!this.plotRect || !this.chart || !this.currentCycle) {
        return { display: 'none' }
      }
      const startTime = this.getTimeValueFromSecond(startSec)
      const endTime = this.getTimeValueFromSecond(endSec)
      const startPixel = this.chart.convertToPixel({ xAxisIndex: 0 }, startTime)
      const endPixel = this.chart.convertToPixel({ xAxisIndex: 0 }, endTime)
      if (!Number.isFinite(startPixel) || !Number.isFinite(endPixel)) {
        return { display: 'none' }
      }
      const rawLeft = Math.min(startPixel, endPixel) - this.plotRect.left
      const rawRight = Math.max(startPixel, endPixel) - this.plotRect.left
      const clampedLeft = Math.max(rawLeft, 0)
      const clampedRight = Math.min(rawRight, this.plotRect.width)
      if (clampedRight <= clampedLeft) {
        return { display: 'none' }
      }
      return {
        left: clampedLeft + 'px',
        width: Math.max(clampedRight - clampedLeft, 2) + 'px'
      }
    },
    getRegionOverlayStyle(region) {
      const style = this.getOverlayPositionStyle(region.startSec, region.endSec)
      if (style.display === 'none') {
        return style
      }
      if (region.overlayColor) {
        style.background = region.overlayColor
      }
      if (region.overlayBorderColor) {
        style.borderColor = region.overlayBorderColor
      }
      return style
    },
    getBlockOverlayStyle(block) {
      const style = this.getOverlayPositionStyle(block.startSec, block.endSec)
      if (style.display === 'none') {
        return style
      }
      if (!this.blockOverlayStyleFn) {
        return style
      }
      return {
        ...style,
        ...(this.blockOverlayStyleFn(block) || {})
      }
    },
    getHoveredBlockMetrics(block) {
      if (!block) {
        return []
      }
      return [
        {
          key: 'readingCount',
          label: 'readingCount',
          value: block.readingCount
        },
        { key: 'meanS1', label: 'meanS1', value: block.meanS1 },
        { key: 'meanS2', label: 'meanS2', value: block.meanS2 },
        { key: 'maxS1', label: 'maxS1', value: block.maxS1 },
        { key: 'maxS2', label: 'maxS2', value: block.maxS2 },
        { key: 'minS1', label: 'minS1', value: block.minS1 },
        { key: 'minS2', label: 'minS2', value: block.minS2 },
        { key: 'slopeS1', label: 'slopeS1', value: block.slopeS1 },
        { key: 'slopeS2', label: 'slopeS2', value: block.slopeS2 },
        { key: 'stdS1', label: 'stdS1', value: block.stdS1 },
        { key: 'stdS2', label: 'stdS2', value: block.stdS2 }
      ]
    },
    formatBlockMetricValue(value) {
      if (value === null || value === undefined || value === '') {
        return '--'
      }
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) {
        return value
      }
      if (Number.isInteger(numericValue)) {
        return numericValue
      }
      return numericValue.toFixed(2)
    },
    getPredictionClassName(prediction) {
      return String(prediction || '').toLowerCase()
    },
    getSensorOptionStyle(sensorOption) {
      return {
        borderColor: sensorOption.border,
        background: sensorOption.fill,
        color: '#f8fafc'
      }
    },
    getOverlayLabelKey(label, fallbackIndex) {
      if (label && typeof label === 'object') {
        return label.text || fallbackIndex
      }
      return label || fallbackIndex
    },
    getOverlayLabelText(label) {
      return label && typeof label === 'object' ? label.text : label
    },
    getOverlayLabelStyle(label) {
      return label && typeof label === 'object' && label.color
        ? { color: label.color }
        : {}
    },
    getOverlayLabelMarkerStyle(label) {
      return label && typeof label === 'object' && label.markerColor
        ? { backgroundColor: label.markerColor }
        : null
    },
    isPointInPlotArea(offsetX, offsetY) {
      if (!this.plotRect) {
        return false
      }
      return (
        offsetX >= this.plotRect.left &&
        offsetX <= this.plotRect.left + this.plotRect.width &&
        offsetY >= this.plotRect.top &&
        offsetY <= this.plotRect.top + this.plotRect.height
      )
    },
    handleZrMouseMove(event) {
      if (this.blockInfoTrigger !== 'hover') {
        return
      }
      if (
        !event ||
        event.offsetX === undefined ||
        event.offsetY === undefined
      ) {
        return
      }
      if (!this.isPointInPlotArea(event.offsetX, event.offsetY)) {
        this.$emit('chart-leave')
        return
      }
      const second = this.getSecondsFromOffsetX(event.offsetX)
      const leftPx = event.offsetX
      this.$emit('chart-hover', {
        second,
        leftPx,
        topPx: event.offsetY,
        chartWidth: this.$refs.chartContainer
          ? this.$refs.chartContainer.clientWidth
          : 0,
        chartHeight: this.$refs.chartContainer
          ? this.$refs.chartContainer.clientHeight
          : 0
      })
    },
    handleZrClick(event) {
      if (this.blockInfoTrigger !== 'click') {
        return
      }
      if (
        !event ||
        event.offsetX === undefined ||
        event.offsetY === undefined
      ) {
        this.$emit('chart-leave')
        return
      }
      if (!this.isPointInPlotArea(event.offsetX, event.offsetY)) {
        this.$emit('chart-leave')
        return
      }
      const second = this.getSecondsFromOffsetX(event.offsetX)
      this.$emit('chart-hover', {
        second,
        leftPx: event.offsetX,
        topPx: event.offsetY,
        chartWidth: this.$refs.chartContainer
          ? this.$refs.chartContainer.clientWidth
          : 0,
        chartHeight: this.$refs.chartContainer
          ? this.$refs.chartContainer.clientHeight
          : 0
      })
    },
    handleZrMouseLeave() {
      if (this.blockInfoTrigger !== 'hover') {
        return
      }
      this.$emit('chart-leave')
    },
    handleRegionRemove(regionId) {
      this.$emit('close-quick-reason-picker')
      this.$emit('remove-region', regionId)
    },
    handleRegionClick(regionId) {
      if (this.readOnly) {
        return
      }
      this.$emit('activate-region', regionId)
    },
    startRegionInteraction(mode, region, event) {
      if (this.readOnly || !region || !event || event.button !== 0) {
        return
      }
      event.preventDefault()
      this.$emit('close-quick-reason-picker')
      this.regionInteraction = {
        mode,
        regionId: region.id,
        pointerStartSec: this.getSecondsFromClientX(event.clientX),
        initialStartSec: region.startSec,
        initialEndSec: region.endSec
      }
      document.addEventListener('mousemove', this.handleRegionInteractionMove)
      document.addEventListener('mouseup', this.stopRegionInteraction)
    },
    handleRegionInteractionMove(event) {
      if (!this.regionInteraction) {
        return
      }
      const nextPointerSec = this.getSecondsFromClientX(event.clientX)
      const deltaSec = nextPointerSec - this.regionInteraction.pointerStartSec
      let nextStartSec = this.regionInteraction.initialStartSec
      let nextEndSec = this.regionInteraction.initialEndSec

      if (this.regionInteraction.mode === 'move') {
        nextStartSec += deltaSec
        nextEndSec += deltaSec
      } else if (this.regionInteraction.mode === 'resize-left') {
        nextStartSec += deltaSec
      } else if (this.regionInteraction.mode === 'resize-right') {
        nextEndSec += deltaSec
      }

      this.$emit('update-region-range', {
        regionId: this.regionInteraction.regionId,
        startSec: nextStartSec,
        endSec: nextEndSec,
        mode: this.regionInteraction.mode
      })
    },
    stopRegionInteraction() {
      document.removeEventListener(
        'mousemove',
        this.handleRegionInteractionMove
      )
      document.removeEventListener('mouseup', this.stopRegionInteraction)
      this.regionInteraction = null
    }
  }
}
</script>

<style lang="scss" scoped>
.panel-card :deep(.el-card__header) {
  padding: 12px 20px;
}

.panel-card :deep(.el-card__body) {
  padding: 12px 16px;
}

.chart-shell {
  position: relative;
  padding: 14px;
  border-radius: 12px;
  background: #0f172a;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.chart-shell-lg {
  min-height: 450px;
}

.chart-loading-overlay {
  position: absolute;
  inset: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.7);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  z-index: 6;
  backdrop-filter: blur(2px);
}

.chart-loading-spinner {
  font-size: 22px;
}

.chart-frame {
  position: relative;
  padding-left: 40px;
}

.chart-frame-echarts {
  padding-left: 0;
}

.echarts-surface {
  height: 420px;
}

.chart-overlay-layer {
  position: absolute;
  cursor: crosshair;
  pointer-events: none;
}

.block-band {
  position: absolute;
  top: 0;
  bottom: 0;
  border-right: 1px dashed rgba(148, 163, 184, 0.2);
  background: rgba(148, 163, 184, 0.04);
  pointer-events: none;
}

.region-overlay {
  position: absolute;
  top: 8px;
  bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.32);
  pointer-events: auto;
  cursor: grab;
}

.region-overlay-active {
  box-shadow: inset 0 0 0 1px rgba(248, 250, 252, 0.75);
}

.region-overlay-read-only {
  cursor: default;
}

.region-resize-handle {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 48px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(248, 250, 252, 0.55);
  transform: translateY(-50%);
  cursor: ew-resize;
  pointer-events: auto;
  z-index: 3;
}

.region-resize-handle-left {
  left: -6px;
}

.region-resize-handle-right {
  right: -6px;
}

.region-remove-button {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  color: #f8fafc;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
  z-index: 2;
}

.region-overlay-label {
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: calc(100% - 30px);
  padding: 2px 6px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font-size: 10px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: auto;
  cursor: pointer;
  z-index: 1;
}

.echarts-surface :deep(.chart-tooltip) {
  min-width: 168px;
}

.echarts-surface :deep(.chart-tooltip-title) {
  margin-bottom: 6px;
  color: #f8fafc;
  font-size: 12px;
  font-weight: 600;
}

.echarts-surface :deep(.chart-tooltip-row) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.echarts-surface :deep(.chart-tooltip-row + .chart-tooltip-row) {
  margin-top: 4px;
}

.echarts-surface :deep(.chart-tooltip-series) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(226, 232, 240, 0.82);
}

.echarts-surface :deep(.chart-tooltip-dot) {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

.region-overlay-label-line {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.region-overlay-label-marker {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 8px;
}

.quick-reason-panel {
  position: absolute;
  width: calc(100% - 40px);
  max-width: 320px;
  padding: 12px;
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.96);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.24);
  z-index: 4;
}

.quick-reason-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.hovered-block-card {
  position: absolute;
  top: 28px;
  right: 28px;
  width: 260px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.35);
  color: #e2e8f0;
  z-index: 7;
  pointer-events: auto;
}

.hovered-block-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.hovered-block-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hovered-block-close-button {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 999px;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}

.hovered-block-close-button:hover {
  border-color: rgba(56, 189, 248, 0.55);
  color: #f8fafc;
}

.hovered-block-prediction {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.hovered-block-prediction.green {
  background: rgba(34, 197, 94, 0.2);
  color: #86efac;
}

.hovered-block-prediction.red {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

.hovered-block-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.hovered-block-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  background: rgba(30, 41, 59, 0.88);
}

.hovered-block-metric-label {
  color: #94a3b8;
  font-size: 11px;
}

.hovered-block-metric-value {
  color: #f8fafc;
  font-size: 12px;
}

.quick-reason-title {
  margin-bottom: 8px;
  color: #f8fafc;
  font-size: 12px;
  font-weight: 600;
}

.quick-reason-title-primary {
  margin-bottom: 0;
}

.quick-reason-subtitle {
  margin-bottom: 8px;
  color: rgba(224, 242, 254, 0.78);
}

.quick-reason-remove-button {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fda4af;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.quick-reason-remove-button:hover {
  color: #fecdd3;
}

.quick-reason-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.quick-sensor-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.quick-sensor-option {
  min-width: 68px;
  padding: 6px 10px;
  border: 1px solid rgba(224, 242, 254, 0.35);
  border-radius: 999px;
  background: rgba(30, 41, 59, 0.92);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.quick-sensor-option.active {
  box-shadow: inset 0 0 0 1px rgba(248, 250, 252, 0.9);
}

.quick-reason-option {
  padding: 6px 10px;
  border: 1px solid rgba(224, 242, 254, 0.35);
  border-radius: 999px;
  background: transparent;
  color: #e0f2fe;
  font-size: 11px;
  cursor: pointer;
}

.quick-reason-option.active {
  border-color: #38bdf8;
  background: rgba(56, 189, 248, 0.18);
  color: #ffffff;
}

.quick-reason-save-button {
  width: 100%;
  min-height: 44px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.quick-reason-save-button:not(.is-disabled) {
  box-shadow: 0 10px 24px rgba(56, 189, 248, 0.28);
}

.quick-reason-save-button:not(.is-disabled):hover,
.quick-reason-save-button:not(.is-disabled):focus {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(56, 189, 248, 0.34);
}

.echarts-surface :deep(.ec-legend-item) {
  cursor: default;
}

.echarts-surface :deep(.ec-legend-text) {
  fill: #e2e8f0;
}

@media (max-width: 1024px) {
  .chart-shell-lg {
    min-height: 380px;
  }

  .echarts-surface {
    height: 340px;
  }
}

@media (max-width: 767px) {
  .echarts-surface {
    height: 260px;
  }
}
</style>
